/**
 * @file Web gallery ad widget discovery page / modal content.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import InputGroup from '../../../components/global/InputGroup';
import { sanitizeJoinCode } from '../../../util/join-code';
import Button from '../../../components/global/Button';
import Alert from '../../../components/global/Alert';
import { getErrorString } from '../../../util/normalize-errors';
import { isAlbumDiscoverable, isAlbumPurchasable } from '../../../util/album';
import { isAlbumFolderDiscoverable } from '../../../util/album-folder';
import { JoinCodeByCodeForAdWidget } from '../graphql/queries/code';

class WebGalleryAdWidgetDiscovery extends Component {
  static propTypes = {
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    joinCodeInput: '',
    loading: false,
    error: '',
  };

  onInputChange = event => {
    const { name, value } = event.target;

    this.setState(() => ({
      [name]: sanitizeJoinCode(value),
    }));
  };

  onFormSubmit = async event => {
    event.preventDefault();
    const { joinCodeInput } = this.state;
    const { client, history } = this.props;

    this.setState(() => ({ loading: true, error: '' }));

    try {
      const {
        data: { joinCodeByCode },
      } = await client.query({
        query: JoinCodeByCodeForAdWidget,
        variables: {
          code: joinCodeInput,
        },
        fetchPolicy: 'network-only',
      });

      const errorMessage =
        'The join code you requested could not be found. ' +
        'Please make sure you have the right code and try again.';

      if (!joinCodeByCode) {
        throw Error(errorMessage);
      }

      const { album_folder: albumFolder, album } = joinCodeByCode;

      if (albumFolder) {
        if (isAlbumFolderDiscoverable(albumFolder)) {
          history.push(`/discover/album-folder/${albumFolder.id}`);
        } else {
          throw Error(errorMessage);
        }
      }

      if (joinCodeByCode.album) {
        if (isAlbumDiscoverable(album) && isAlbumPurchasable(album)) {
          history.push(`/purchase/album/${album.id}`);
        } else {
          throw Error(errorMessage);
        }
      }
    } catch (error) {
      this.setState(() => ({ loading: false, error: getErrorString(error) }));
    }
  };

  render() {
    const { joinCodeInput, loading, error } = this.state;

    return (
      <div>
        <form method="post" onSubmit={this.onFormSubmit}>
          <p className="text-center mb-4">
            Enter your join code so Waldo can then onboard you to the right
            album or gallery and find your matching photos!
          </p>

          {error && <Alert type="danger">{error}</Alert>}

          <InputGroup
            addon="#"
            id="ad-join-code"
            name="joinCodeInput"
            label="Enter your join code"
            labelSrOnly
            placeholder="e.g. SummerMadness"
            inputClassName="d-inline"
            value={joinCodeInput}
            onChange={this.onInputChange}
            required
          />

          <Button className="m-0" type="submit" btnBlock disabled={loading}>
            {loading ? 'Loading...' : 'Join album'}
          </Button>
        </form>
      </div>
    );
  }
}

export default WebGalleryAdWidgetDiscovery;
